import { useEffect } from 'react';
import { ClipboardCheck, Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRetentionActions } from '@/hooks/use-retention-actions';
import { ChurnRiskScore } from '@/lib/churn-risk';
import { RetentionActionCard } from './RetentionActionCard';

interface ClientRetentionActionsProps {
  clientId: string;
  clientName: string;
  riskScore: ChurnRiskScore | null;
  organizationId?: string | null;
  className?: string;
}

export function ClientRetentionActions({
  clientId,
  clientName,
  riskScore,
  organizationId,
  className,
}: ClientRetentionActionsProps) {
  const {
    actions,
    loading,
    syncActions,
    completeAction,
    snoozeAction,
    dismissAction,
  } = useRetentionActions(clientId);

  // Sync actions whenever the risk score changes
  useEffect(() => {
    if (riskScore && clientName) {
      syncActions(clientName, riskScore, organizationId);
    }
  }, [riskScore, clientName, organizationId, syncActions]);

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="h-5 w-5" />
            Action Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (actions.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="h-5 w-5" />
            Action Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              No action items for this client
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="h-5 w-5" />
            Action Items
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {actions.length} pending
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {actions.map(action => (
            <RetentionActionCard
              key={action.id}
              action={action}
              onComplete={completeAction}
              onSnooze={snoozeAction}
              onDismiss={dismissAction}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
